import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import html2canvas from 'html2canvas';
import ExcelJS from 'exceljs';
import { collection, getDocs, getDoc, doc, query, where } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { Emogiface, Emogiface2, Emogiface3, Emogiface4, Emogiface5, Emogiface6 } from '../../../tools/MyIcons';

import {
  ThemeProvider, createTheme, CssBaseline,
  Box, Typography, Card, CardContent, Chip,
  LinearProgress, Grid, Stack, CircularProgress,
  Alert, Fade, Grow, Button, Collapse, Divider,
  Avatar, IconButton, Tooltip,
} from '@mui/material';
import BarChartIcon           from '@mui/icons-material/BarChart';
import PeopleAltIcon          from '@mui/icons-material/PeopleAlt';
import StarRoundedIcon        from '@mui/icons-material/StarRounded';
import QuizIcon               from '@mui/icons-material/Quiz';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import TuneIcon               from '@mui/icons-material/Tune';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TextFieldsIcon         from '@mui/icons-material/TextFields';
import ChatBubbleOutlineIcon  from '@mui/icons-material/ChatBubbleOutline';
import CommentIcon            from '@mui/icons-material/Comment';
import ExpandMoreIcon         from '@mui/icons-material/ExpandMore';
import ExpandLessIcon         from '@mui/icons-material/ExpandLess';
import CheckCircleIcon        from '@mui/icons-material/CheckCircle';
import CancelIcon             from '@mui/icons-material/Cancel';
import AccessTimeIcon         from '@mui/icons-material/AccessTime';
import WbSunnyRoundedIcon     from '@mui/icons-material/WbSunnyRounded';
import DarkModeRoundedIcon    from '@mui/icons-material/DarkModeRounded';
import SortIcon               from '@mui/icons-material/Sort';
import FilterListIcon         from '@mui/icons-material/FilterList';
import ViewModuleIcon         from '@mui/icons-material/ViewModule';
import TableRowsIcon          from '@mui/icons-material/TableRows';
import TrendingUpIcon         from '@mui/icons-material/TrendingUp';
import TrendingDownIcon       from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon       from '@mui/icons-material/TrendingFlat';
import DownloadRoundedIcon    from '@mui/icons-material/DownloadRounded';
import TableChartIcon         from '@mui/icons-material/TableChart';

// ─── i18n ─────────────────────────────────────────────────────────────────────
const TRANSLATIONS = {
  en: {
    filterAll: 'All',
    statResponses:  'Responses',
    statAverage:    'Global avg.',
    statQuestions:  'Questions',
    typeRating:       'Rating',
    typeEmoji:        'Emoji',
    typeEmojiCat:     'Emoji Cat',
    typeEmojiPeople:  'Emoji People',
    typeRadio:        'Multiple choice',
    typeSlider:       'Range',
    typeText:         'Free text',
    resp:             'resp.',
    textAnswersNote:  'Individual answers in the',
    textAnswersNoteSuffix: 'collection.',
    typeUnsupported:  'Type not supported',
    commentsEnabled:   'Comments enabled',
    commentsDisabled:  'Comments disabled',
    commentsEnabledDesc:  'Participants can leave comments on this survey.',
    commentsDisabledDesc: 'The comments option is disabled for this survey.',
    showComments:      'View comments',
    hideComments:      'Hide comments',
    noComments:        'No comments in this period.',
    loading:           'Loading results…',
    errorSurvey:       'Survey not found.',
    errorGeneric:      'Could not load results.',
    noData:            'No responses yet for this survey.',
    surveyTitle:       'Survey Results',
    toggleLight:       'Switch to light mode',
    toggleDark:        'Switch to dark mode',
    sortByOrder:       'Original order',
    sortByResult:      'Sort by result',
    sortTooltip:       'Sort questions by most-selected answer (1 → 5)',
    dominantLabel:     'Most selected',
    // ── Analytical strings ──
    viewCards:         'Cards',
    viewTable:         'Table',
    healthGood:        'On track',
    healthFair:        'Average',
    healthPoor:        'Needs attention',
    vsLastPeriod:      'vs prev. period',
    filterByHealth:    'Health:',
    healthAll:         'All',
    colQ:              'Q',
    colQuestion:       'Question',
    colType:           'Type',
    colDist:           'Distribution',
    colScore:          'Score',
    colDominant:       'Top',
    colResponses:      'Resp.',
    allOnTrack:        'All questions on track',
    deltaUp:           'Improved',
    deltaDown:         'Declined',
    deltaFlat:         'No change',
    noScore:           'N/A',
    downloadPng:       'Download PNG',
    exportExcel:       'Export Excel',
    excelSheetName:    'Results',
    excelSurvey:       'Survey',
    excelPeriod:       'Period',
    excelExported:     'Exported',
    excelAnswer:       'Answer',
    excelFace:         'Face',
    months: ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'],
  },
  es: {
    filterAll: 'Todo',
    statResponses:  'Respuestas',
    statAverage:    'Promedio global',
    statQuestions:  'Preguntas',
    typeRating:       'Calificación',
    typeEmoji:        'Emoji',
    typeEmojiCat:     'Emoji Cat',
    typeEmojiPeople:  'Emoji People',
    typeRadio:        'Múltiple',
    typeSlider:       'Rango',
    typeText:         'Texto libre',
    resp:             'resp.',
    textAnswersNote:  'Respuestas individuales en la colección',
    textAnswersNoteSuffix: '.',
    typeUnsupported:  'Tipo no soportado',
    commentsEnabled:   'Comentarios activados',
    commentsDisabled:  'Comentarios desactivados',
    commentsEnabledDesc:  'Los participantes pueden dejar comentarios en este survey.',
    commentsDisabledDesc: 'La opción de comentarios está deshabilitada para este survey.',
    showComments:      'Ver comentarios',
    hideComments:      'Ocultar comentarios',
    noComments:        'No hay comentarios en este período.',
    loading:           'Cargando resultados…',
    errorSurvey:       'Survey no encontrado.',
    errorGeneric:      'No se pudieron cargar los resultados.',
    noData:            'Aún no hay respuestas para este survey.',
    surveyTitle:       'Resultados del Survey',
    toggleLight:       'Cambiar a modo claro',
    toggleDark:        'Cambiar a modo oscuro',
    sortByOrder:       'Orden original',
    sortByResult:      'Ordenar por resultado',
    sortTooltip:       'Ordena las preguntas por la respuesta más seleccionada (1 → 5)',
    dominantLabel:     'Más seleccionado',
    // ── Analytical strings ──
    viewCards:         'Tarjetas',
    viewTable:         'Tabla',
    healthGood:        'En orden',
    healthFair:        'Regular',
    healthPoor:        'Atención',
    vsLastPeriod:      'vs período ant.',
    filterByHealth:    'Estado:',
    healthAll:         'Todo',
    colQ:              'P',
    colQuestion:       'Pregunta',
    colType:           'Tipo',
    colDist:           'Distribución',
    colScore:          'Puntaje',
    colDominant:       'Top',
    colResponses:      'Resp.',
    allOnTrack:        'Todas las preguntas en orden',
    deltaUp:           'Mejoró',
    deltaDown:         'Bajó',
    deltaFlat:         'Sin cambio',
    noScore:           'N/A',
    downloadPng:       'Descargar PNG',
    exportExcel:       'Exportar Excel',
    excelSheetName:    'Resultados',
    excelSurvey:       'Survey',
    excelPeriod:       'Período',
    excelExported:     'Exportado',
    excelAnswer:       'Respuesta',
    excelFace:         'Cara',
    months: ['','Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'],
  },
};

const detectLang = () => {
  const lang = (navigator?.language || navigator?.languages?.[0] || 'en').toLowerCase();
  return lang.startsWith('es') ? 'es' : 'en';
};

const useTranslations = () => {
  const lang = useMemo(() => detectLang(), []);
  return TRANSLATIONS[lang] ?? TRANSLATIONS.en;
};

// ─── Palette helper ───────────────────────────────────────────────────────────
const getPalette = (mode) => {
  const isDark = mode === 'dark';
  return {
    isDark,
    pageBg: isDark
      ? ['radial-gradient(ellipse at 15% 0%, rgba(108,99,255,0.13) 0%, transparent 50%)',
         'radial-gradient(ellipse at 85% 100%, rgba(255,101,132,0.09) 0%, transparent 50%)',
         '#0D0F1A'].join(', ')
      : ['radial-gradient(ellipse at 15% 0%, rgba(108,99,255,0.07) 0%, transparent 50%)',
         'radial-gradient(ellipse at 85% 100%, rgba(255,101,132,0.05) 0%, transparent 50%)',
         '#F4F5FF'].join(', '),
    paperBg: isDark ? '#151828' : '#FFFFFF',
    cardBg: (accent) => isDark
      ? `linear-gradient(160deg, ${accent}14 0%, #151828 55%)`
      : `linear-gradient(160deg, ${accent}0C 0%, #FFFFFF 60%)`,
    statCardBg: (color) => isDark
      ? `linear-gradient(135deg, ${color}22 0%, #15182800 70%)`
      : `linear-gradient(135deg, ${color}14 0%, #FFFFFF00 70%)`,
    statCardBorder: (color) => isDark
      ? `1px solid ${color}38 !important`
      : `1px solid ${color}50 !important`,
    commentText: isDark ? '#EEF0FF' : '#1A1C2E',
    timeColor:   isDark ? '#4A4F6A' : '#9CA3AF',
    commentBoxBg:     isDark ? 'rgba(21,24,40,0.7)'      : 'rgba(255,255,255,0.92)',
    commentBoxBorder: isDark ? '1px solid rgba(108,99,255,0.22)' : '1px solid rgba(108,99,255,0.15)',
    commentBubbleBg:  isDark ? 'rgba(255,255,255,0.04)'   : 'rgba(0,0,0,0.025)',
    commentBubbleBorder: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.07)',
    commentBubbleHover:  isDark ? 'rgba(108,99,255,0.07)' : 'rgba(108,99,255,0.05)',
    timelineLineGrad: isDark
      ? 'linear-gradient(to bottom, #6C63FF66, transparent)'
      : 'linear-gradient(to bottom, #6C63FF44, transparent)',
    emojiActiveBg:     isDark ? 'rgba(108,99,255,0.12)' : 'rgba(108,99,255,0.07)',
    emojiActiveBorder: isDark ? '1px solid rgba(108,99,255,0.3)' : '1px solid rgba(108,99,255,0.25)',
    emojiInactiveBg:   isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
    emojiInactiveBorder: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.07)',
    progressTrack: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.07)',
    miscChipBg:  isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
    dividerColor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.09)',
    ratingBadgeBg:     isDark ? 'rgba(249,199,79,0.1)'  : 'rgba(249,199,79,0.12)',
    ratingBadgeBorder: isDark ? '1px solid rgba(249,199,79,0.3)' : '1px solid rgba(249,199,79,0.4)',
    sliderBadgeBg:     isDark ? 'rgba(108,99,255,0.1)'  : 'rgba(108,99,255,0.08)',
    sliderBadgeBorder: isDark ? '1px solid rgba(108,99,255,0.3)' : '1px solid rgba(108,99,255,0.3)',
    headerLogoBg: 'linear-gradient(135deg, #6C63FF, #FF6584)',
    toggleBg:     isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)',
    toggleHover:  isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.12)',
    toggleColor:  isDark ? '#F9C74F' : '#6C63FF',
    // ── Analytical ──
    insightsBg:     isDark ? 'rgba(255,255,255,0.025)' : 'rgba(108,99,255,0.04)',
    insightsBorder: isDark ? 'rgba(255,255,255,0.07)'  : 'rgba(108,99,255,0.12)',
    tableRowHover:  isDark ? 'rgba(255,255,255,0.025)' : 'rgba(108,99,255,0.03)',
    tableHeaderBg:  isDark ? 'rgba(255,255,255,0.03)'  : 'rgba(108,99,255,0.04)',
  };
};

// ─── Theme factory ────────────────────────────────────────────────────────────
const buildTheme = (mode) => createTheme({
  palette: {
    mode,
    primary:    { main: '#6C63FF' },
    secondary:  { main: '#FF6584' },
    success:    { main: '#43E97B' },
    warning:    { main: '#F9C74F' },
    error:      { main: '#FF6B6B' },
    background: mode === 'dark'
      ? { default: '#0D0F1A', paper: '#151828' }
      : { default: '#F4F5FF', paper: '#FFFFFF'  },
    text: mode === 'dark'
      ? { primary: '#EEF0FF', secondary: '#8B90B8' }
      : { primary: '#1A1C2E', secondary: '#6B7280' },
  },
  typography: {
    fontFamily: "'Outfit', 'Segoe UI', sans-serif",
    h5:        { fontWeight: 800, letterSpacing: '-0.02em' },
    subtitle2: { fontWeight: 600 },
    caption:   { letterSpacing: '0.06em' },
  },
  shape: { borderRadius: 16 },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: mode === 'dark'
            ? '1px solid rgba(108,99,255,0.12)'
            : '1px solid rgba(108,99,255,0.14)',
          boxShadow: mode === 'dark'
            ? 'none'
            : '0 1px 8px rgba(108,99,255,0.06)',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 99,
          height: 8,
          backgroundColor: mode === 'dark'
            ? 'rgba(255,255,255,0.06)'
            : 'rgba(0,0,0,0.07)',
        },
      },
    },
    MuiChip: { styleOverrides: { root: { transition: 'all 0.2s' } } },
  },
});

// ─── Emoji sets ───────────────────────────────────────────────────────────────
const EMOJI_SETS = {
  emojis:        Emogiface,
  emojis2:       Emogiface2,
  emojisCat:     Emogiface3,
  emojispeople:  Emogiface4,
  emojisnumbers: Emogiface5,
  emojislike:    Emogiface6,
};

const getEmojiSrc = (type, val) => EMOJI_SETS[type]?.[`face${val}`] || null;

// ─── Constants ────────────────────────────────────────────────────────────────
const RATING_GRADIENT = ['#FF6B6B','#FF9F43','#F9C74F','#A8E063','#43E97B'];
const RADIO_COLORS    = ['#6C63FF','#FF6584','#43E97B','#F9C74F','#38BDF8'];

const CARD_ACCENT = {
  rating:        '#6C63FF',
  emojis:        '#FF6584',
  emojis2:       '#FF6584',
  emojisCat:     '#FF6584',
  emojispeople:  '#FF6584',
  emojisnumbers: '#FF6584',
  emojislike:    '#FF6584',
  radio:         '#43E97B',
  slider:        '#38BDF8',
  text:          '#8B90B8',
};

// ─── Health score system ─────────────────────────────────────────────────────
const getScoreHealth = (avg) => {
  if (avg === null || avg === undefined) return 'neutral';
  const n = parseFloat(avg);
  if (isNaN(n) || n === 0) return 'neutral';
  if (n >= 4)   return 'good';
  if (n >= 3)   return 'fair';
  return 'poor';
};

const HEALTH_CONFIG = {
  good:    { color: '#43E97B', bg: 'rgba(67,233,123,0.12)',   border: 'rgba(67,233,123,0.35)'  },
  fair:    { color: '#F9C74F', bg: 'rgba(249,199,79,0.12)',   border: 'rgba(249,199,79,0.35)'  },
  poor:    { color: '#FF6B6B', bg: 'rgba(255,107,107,0.12)', border: 'rgba(255,107,107,0.35)' },
  neutral: { color: '#8B90B8', bg: 'rgba(139,144,184,0.10)', border: 'rgba(139,144,184,0.30)' },
};

// ─── Type labels & icons (module-level for reuse) ─────────────────────────────
const TYPE_LABELS = {
  rating:        'typeRating',
  emojis:        'typeEmoji',
  emojis2:       'typeEmoji',
  emojisCat:     'typeEmojiCat',
  emojispeople:  'typeEmojiPeople',
  emojisnumbers: 'typeEmoji',
  emojislike:    'typeEmoji',
  radio:         'typeRadio',
  slider:        'typeSlider',
  text:          'typeText',
};

const TYPE_ICONS = {
  rating:        <StarRoundedIcon sx={{ fontSize: 11 }} />,
  emojis:        <SentimentSatisfiedIcon sx={{ fontSize: 11 }} />,
  emojis2:       <SentimentSatisfiedIcon sx={{ fontSize: 11 }} />,
  emojisCat:     <SentimentSatisfiedIcon sx={{ fontSize: 11 }} />,
  emojispeople:  <SentimentSatisfiedIcon sx={{ fontSize: 11 }} />,
  emojisnumbers: <SentimentSatisfiedIcon sx={{ fontSize: 11 }} />,
  emojislike:    <SentimentSatisfiedIcon sx={{ fontSize: 11 }} />,
  radio:         <CheckCircleOutlineIcon sx={{ fontSize: 11 }} />,
  slider:        <TuneIcon sx={{ fontSize: 11 }} />,
  text:          <TextFieldsIcon sx={{ fontSize: 11 }} />,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const periodKeyFromData = (data) => {
  const anio  = data.anio ?? '';
  const month = String(data.month ?? '').padStart(2, '0');
  return `${anio}_${month}`;
};

const periodLabel = (key, months) => {
  const [year, month] = key.split('_');
  return `${months[parseInt(month, 10)]} ${year}`;
};

const currentPeriodKey = () => {
  const now = new Date();
  return `${now.getFullYear()}_${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const previousPeriodKey = (key) => {
  if (!key || key === 'all') return null;
  const [year, month] = key.split('_').map(Number);
  if (month === 1) return `${year - 1}_12`;
  return `${year}_${String(month - 1).padStart(2, '0')}`;
};

const formatDate = (ts) => {
  if (!ts) return '';
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  const lang = detectLang();
  return d.toLocaleString(lang === 'es' ? 'es-MX' : 'en-US', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const uidInitials = (uid = '') => uid.slice(-2).toUpperCase();
const stringColor = (str = '') => {
  const palette = ['#6C63FF','#FF6584','#43E97B','#F9C74F','#38BDF8','#FB923C','#A78BFA'];
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return palette[Math.abs(hash) % palette.length];
};

const aggregateGroups = (surveyQuestions, sumDocs) => {
  const meta = {};
  Object.values(surveyQuestions || {}).forEach((q) => {
    const o = q.orden ?? 0;
    if (!meta[o]) meta[o] = q;
  });
  const acc = {};
  sumDocs.forEach((d) => {
    const o = d.orden ?? 0;
    if (!acc[o]) {
      acc[o] = {
        orden: o, text: d.text || meta[o]?.text || `Question ${o}`,
        type: meta[o]?.type || 'rating', options: meta[o]?.options || [],
        sliderConfig: meta[o]?.sliderConfig || null,
        url: meta[o]?.url || '',
        counts: { 1:0, 2:0, 3:0, 4:0, 5:0 },
      };
    }
    for (let i = 1; i <= 5; i++) acc[o].counts[i] += d[`ans_${i}`] || 0;
  });
  return Object.values(acc)
    .map((g) => ({ ...g, total: Object.values(g.counts).reduce((s,n)=>s+n,0) }))
    .sort((a, b) => a.orden - b.orden);
};

const weightedAvg = (counts, total) => {
  if (!total) return null;
  const sum = Object.entries(counts).reduce((s,[k,v]) => s + Number(k)*v, 0);
  return (sum / total).toFixed(2);
};

const dominantAnswer = (counts) => {
  let best = 99, bestCount = 0;
  for (let i = 1; i <= 5; i++) {
    const c = counts[i] || 0;
    if (c > bestCount || (c === bestCount && c > 0 && i < best)) {
      bestCount = c;
      best = i;
    }
  }
  return best;
};

const computeGlobalAvg = (groups) => {
  const supported = groups.filter(g => g.type !== 'text' && g.total > 0);
  const avgs = supported
    .map(g => weightedAvg(g.counts, g.total))
    .filter(Boolean)
    .map(parseFloat);
  return avgs.length ? (avgs.reduce((s,n)=>s+n,0)/avgs.length).toFixed(2) : null;
};

// ─── MiniDistribution ─────────────────────────────────────────────────────────
const MiniDistribution = ({ counts, total, height = 8 }) => {
  if (!total) return <Box sx={{ height, borderRadius: 99, background: 'rgba(128,128,128,0.1)', width: '100%' }} />;
  return (
    <Tooltip
      title={
        <Box sx={{ fontSize: '0.7rem' }}>
          {[1,2,3,4,5].map(v => {
            const c = counts[v] || 0;
            const pct = Math.round((c/total)*100);
            return c > 0 ? (
              <Box key={v} sx={{ display:'flex', gap:1, alignItems:'center' }}>
                <Box sx={{ width:8, height:8, borderRadius:'50%', background: RATING_GRADIENT[v-1], flexShrink:0 }} />
                <span>{v}: {c} ({pct}%)</span>
              </Box>
            ) : null;
          })}
        </Box>
      }
      placement="top"
      arrow
    >
      <Stack
        direction="row"
        sx={{ height, borderRadius: 99, overflow: 'hidden', gap: '1px', width: '100%', cursor: 'default' }}
      >
        {[1,2,3,4,5].map((val) => {
          const pct = (counts[val] || 0) / total * 100;
          if (!pct) return null;
          return (
            <Box key={val} sx={{ width: `${pct}%`, background: RATING_GRADIENT[val-1], minWidth: pct > 0 ? 2 : 0 }} />
          );
        })}
      </Stack>
    </Tooltip>
  );
};

// ─── ScoreBadge ──────────────────────────────────────────────────────────────
const ScoreBadge = ({ avg, max = 5, size = 'sm' }) => {
  const health = getScoreHealth(avg);
  const hc = HEALTH_CONFIG[health];
  const isLg = size === 'lg';
  if (!avg) return <Typography variant="caption" color="text.secondary">—</Typography>;
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'baseline', gap: 0.3,
      px: isLg ? 1.5 : 1, py: isLg ? 0.6 : 0.3,
      borderRadius: 2.5,
      background: hc.bg,
      border: `1px solid ${hc.border}`,
    }}>
      <Typography sx={{ fontSize: isLg ? '1rem' : '0.78rem', fontWeight: 800, color: hc.color, lineHeight: 1 }}>
        {avg}
      </Typography>
      <Typography sx={{ fontSize: isLg ? '0.65rem' : '0.58rem', color: hc.color, opacity: 0.7, lineHeight: 1 }}>
        /{max}
      </Typography>
    </Box>
  );
};

// ─── HealthDot ────────────────────────────────────────────────────────────────
const HealthDot = ({ avg, size = 8 }) => {
  const hc = HEALTH_CONFIG[getScoreHealth(avg)];
  return (
    <Box sx={{
      width: size, height: size, borderRadius: '50%',
      background: hc.color,
      boxShadow: `0 0 6px ${hc.color}66`,
      flexShrink: 0,
    }} />
  );
};

// ─── DeltaBadge ──────────────────────────────────────────────────────────────
const DeltaBadge = ({ delta, label }) => {
  if (delta === null || delta === undefined) return null;
  const n = parseFloat(delta);
  const isUp   = n > 0;
  const isDown = n < 0;
  const color  = isUp ? '#43E97B' : isDown ? '#FF6B6B' : '#8B90B8';
  const Icon   = isUp ? TrendingUpIcon : isDown ? TrendingDownIcon : TrendingFlatIcon;
  return (
    <Tooltip title={label} placement="top">
      <Stack direction="row" alignItems="center" spacing={0.3}
        sx={{ px: 0.8, py: 0.2, borderRadius: 1.5, background: `${color}18`, border: `1px solid ${color}35`, flexShrink: 0 }}>
        <Icon sx={{ fontSize: 11, color }} />
        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color }}>
          {isUp ? '+' : ''}{n.toFixed(2)}
        </Typography>
      </Stack>
    </Tooltip>
  );
};

// ─── ControlPill ───────────────────────────────────────────────────────────────
const ControlPill = ({ options, value, onChange }) => (
  <Stack
    direction="row"
    spacing={0}
    sx={{
      borderRadius: 2, overflow: 'hidden',
      border: '1px solid rgba(108,99,255,0.22)',
      flexShrink: 0,
    }}
  >
    {options.map(({ key, icon, label, tooltip }) => {
      const active = value === key;
      const content = (
        <Box
          key={key}
          onClick={() => onChange(key)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.5,
            px: { xs: 1, sm: 1.4 }, py: 0.7,
            cursor: 'pointer',
            fontSize: '0.7rem', fontWeight: active ? 700 : 500,
            color: active ? '#fff' : 'text.secondary',
            background: active
              ? 'linear-gradient(135deg, #6C63FF, #9C63FF)'
              : 'rgba(108,99,255,0.03)',
            transition: 'all 0.2s',
            userSelect: 'none',
            whiteSpace: 'nowrap',
            '&:hover': !active ? {
              background: 'rgba(108,99,255,0.09)',
              color: '#6C63FF',
            } : {},
          }}
        >
          {icon}
          <Typography sx={{ fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit' }}>
            {label}
          </Typography>
        </Box>
      );
      return tooltip
        ? <Tooltip key={key} title={tooltip} placement="top">{content}</Tooltip>
        : <React.Fragment key={key}>{content}</React.Fragment>;
    })}
  </Stack>
);

// ─── HealthFilter ─────────────────────────────────────────────────────────────
const HealthFilter = ({ value, onChange, healthCounts, t, p }) => {
  const opts = [
    { key: 'all',  label: t.healthAll,  color: '#8B90B8' },
    { key: 'good', label: `≥4.0`,       color: '#43E97B', count: healthCounts.good },
    { key: 'fair', label: `3–4`,        color: '#F9C74F', count: healthCounts.fair },
    { key: 'poor', label: `<3.0`,       color: '#FF6B6B', count: healthCounts.poor },
  ].filter(o => o.key === 'all' || o.count > 0);

  if (opts.length <= 1) return null;

  return (
    <Stack direction="row" alignItems="center" spacing={0.8} flexWrap="wrap">
      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, fontSize: '0.68rem' }}>
        {t.filterByHealth}
      </Typography>
      {opts.map(({ key, label, color, count }) => {
        const active = value === key;
        return (
          <Chip
            key={key}
            size="small"
            label={count !== undefined ? `${label} (${count})` : label}
            onClick={() => onChange(key)}
            sx={{
              height: 22, fontSize: '0.64rem', fontWeight: active ? 700 : 500,
              background: active ? `${color}20` : 'transparent',
              color: active ? color : 'text.secondary',
              border: active ? `1px solid ${color}55` : `1px solid ${p.dividerColor}`,
              '&:hover': { background: `${color}15`, color },
            }}
          />
        );
      })}
    </Stack>
  );
};

// ─── Compact table view ───────────────────────────────────────────────────────
const CompactTable = ({ groups, t, p, surveyTitle, periodLabelText }) => {
  const colSx = (w, align = 'left') => ({
    width: w, minWidth: w, flexShrink: 0,
    display: 'flex', alignItems: 'center',
    justifyContent: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start',
  });

  const handleExportExcel = useCallback(async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'EmojiSurveys';
      workbook.created = new Date();

      const sheet = workbook.addWorksheet(t.excelSheetName, {
        views: [{ state: 'frozen', ySplit: 1 }],
      });

      sheet.columns = [
        { header: t.colQ,          key: 'q',       width: 6  },
        { header: t.colQuestion,   key: 'question',width: 50 },
        { header: t.colType,       key: 'type',    width: 16 },
        { header: `${t.excelFace} 1`, key: 'a1',    width: 10 },
        { header: `${t.excelFace} 2`, key: 'a2',    width: 10 },
        { header: `${t.excelFace} 3`, key: 'a3',    width: 10 },
        { header: `${t.excelFace} 4`, key: 'a4',    width: 10 },
        { header: `${t.excelFace} 5`, key: 'a5',    width: 10 },
        { header: t.colScore,      key: 'score',   width: 10 },
        { header: t.colResponses,  key: 'resp',    width: 10 },
      ];

      // Style header row
      const headerRow = sheet.getRow(1);
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF6C63FF' } };
      headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
      headerRow.height = 22;

      groups.forEach((g, i) => {
        const avg = g.type !== 'text' ? weightedAvg(g.counts, g.total) : null;
        const typeLabel = t[TYPE_LABELS[g.type]] || g.type;
        const row = sheet.addRow({
          q: i + 1,
          question: g.text,
          type: typeLabel,
          a1: g.type !== 'text' ? (g.counts[1] || 0) : '',
          a2: g.type !== 'text' ? (g.counts[2] || 0) : '',
          a3: g.type !== 'text' ? (g.counts[3] || 0) : '',
          a4: g.type !== 'text' ? (g.counts[4] || 0) : '',
          a5: g.type !== 'text' ? (g.counts[5] || 0) : '',
          score: avg ? Number(avg) : '',
          resp: g.total,
        });
        row.alignment = { vertical: 'middle', wrapText: true };
        row.getCell('question').alignment = { vertical: 'middle', wrapText: true, horizontal: 'left' };
        ['q','type','a1','a2','a3','a4','a5','score','resp'].forEach((key) => {
          row.getCell(key).alignment = { vertical: 'middle', horizontal: 'center' };
        });
        if (i % 2 === 1) {
          row.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF4F5FF' } };
          });
        }
      });

      sheet.eachRow((row) => {
        row.eachCell((cell) => {
          cell.border = {
            top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
            left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
            bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
            right: { style: 'thin', color: { argb: 'FFE0E0E0' } },
          };
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const safeTitle = (surveyTitle || 'survey')
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^\w\s-]/g, '').trim().slice(0, 40).replace(/\s+/g, '_');
      const safePeriod = (periodLabelText || '')
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '_');
      const link = document.createElement('a');
      link.href = url;
      link.download = `${safeTitle}${safePeriod ? '_' + safePeriod : ''}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting Excel:', err);
    }
  }, [groups, t, surveyTitle, periodLabelText]);

  return (
    <Fade in timeout={400}>
      <Box>
        <Stack direction="row" justifyContent="flex-end" mb={1.2}>
          <Box
            onClick={handleExportExcel}
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.7,
              height: 32, px: 1.5,
              borderRadius: 2,
              cursor: 'pointer',
              color: '#fff',
              background: 'linear-gradient(135deg, #43E97B, #2FBF66)',
              boxShadow: '0 3px 10px rgba(67,233,123,0.4)',
              transition: 'all 0.2s',
              userSelect: 'none',
              '&:hover': { transform: 'translateY(-1px) scale(1.03)', boxShadow: '0 5px 14px rgba(67,233,123,0.5)' },
              '&:active': { transform: 'scale(0.97)' },
            }}
          >
            <TableChartIcon sx={{ fontSize: 17 }} />
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#fff' }}>
              {t.exportExcel}
            </Typography>
          </Box>
        </Stack>
        <Box sx={{ overflowX: 'auto', borderRadius: 3, border: `1px solid ${p.dividerColor}` }}>
        {/* Table header */}
        <Stack direction="row" alignItems="center" sx={{
          px: { xs: 1.5, sm: 2 }, py: 1,
          background: p.tableHeaderBg,
          borderBottom: `1px solid ${p.dividerColor}`,
          minWidth: 560,
          gap: 1,
        }}>
          <Box sx={colSx(32)}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
              {t.colQ}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 160 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
              {t.colQuestion}
            </Typography>
          </Box>
          <Box sx={{ ...colSx(72), display: { xs: 'none', sm: 'flex' } }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
              {t.colType}
            </Typography>
          </Box>
          <Box sx={{ ...colSx(140), display: { xs: 'none', md: 'flex' } }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
              {t.colDist}
            </Typography>
          </Box>
          <Box sx={colSx(68, 'center')}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
              {t.colScore}
            </Typography>
          </Box>
          <Box sx={colSx(44, 'right')}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
              {t.colResponses}
            </Typography>
          </Box>
        </Stack>

        {/* Rows */}
        <Box sx={{ minWidth: 560 }}>
          {groups.map((g, i) => {
            const avg    = g.type !== 'text' ? weightedAvg(g.counts, g.total) : null;
            const health = getScoreHealth(avg);
            const hc     = HEALTH_CONFIG[health];
            const typeLabel = t[TYPE_LABELS[g.type]] || g.type;

            return (
              <Fade key={g.orden} in timeout={350} style={{ transitionDelay: `${i * 25}ms` }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  sx={{
                    px: { xs: 1.5, sm: 2 }, py: 1.4,
                    borderBottom: i < groups.length - 1 ? `1px solid ${p.dividerColor}` : 'none',
                    borderLeft: `3px solid ${hc.color}`,
                    transition: 'background 0.15s',
                    '&:hover': { background: p.tableRowHover },
                  }}
                >
                  {/* Q# */}
                  <Box sx={colSx(32)}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: hc.color }}>
                      {i + 1}
                    </Typography>
                  </Box>

                  {/* Question text */}
                  <Tooltip title={g.text} placement="top-start">
                    <Box sx={{ flex: 1, minWidth: 160 }}>
                      <Typography variant="body2" sx={{
                        fontSize: '0.82rem', lineHeight: 1.4,
                        overflow: 'hidden', textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}>
                        {g.text}
                      </Typography>
                    </Box>
                  </Tooltip>

                  {/* Type */}
                  <Box sx={{ ...colSx(72), display: { xs: 'none', sm: 'flex' } }}>
                    <Chip
                      icon={TYPE_ICONS[g.type]}
                      label={typeLabel}
                      size="small"
                      sx={{
                        fontSize: '0.58rem', height: 18,
                        background: `${CARD_ACCENT[g.type] || '#6C63FF'}18`,
                        color: CARD_ACCENT[g.type] || '#6C63FF',
                        border: `1px solid ${CARD_ACCENT[g.type] || '#6C63FF'}30`,
                        '& .MuiChip-icon': { color: CARD_ACCENT[g.type] || '#6C63FF', ml: 0.4 },
                        '& .MuiChip-label': { px: 0.6 },
                      }}
                    />
                  </Box>

                  {/* Distribution */}
                  <Box sx={{ ...colSx(140), display: { xs: 'none', md: 'flex' }, pr: 1 }}>
                    {g.type !== 'text'
                      ? <MiniDistribution counts={g.counts} total={g.total} height={7} />
                      : <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>—</Typography>
                    }
                  </Box>

                  {/* Score badge */}
                  <Box sx={colSx(68, 'center')}>
                    {avg
                      ? <ScoreBadge avg={avg} />
                      : <Typography variant="caption" color="text.secondary">—</Typography>
                    }
                  </Box>

                  {/* Responses */}
                  <Box sx={colSx(44, 'right')}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {g.total}
                    </Typography>
                  </Box>
                </Stack>
              </Fade>
            );
          })}
        </Box>
      </Box>
      </Box>
    </Fade>
  );
};

// ─── Stat card ────────────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value, color, delay = 0, delta = null, deltaLabel = '', p }) => (
  <Fade in timeout={600} style={{ transitionDelay: `${delay}ms` }}>
    <Card sx={{
      flex: 1, minWidth: 0,
      background: p.statCardBg(color),
      border: p.statCardBorder(color),
      transition: 'transform 0.2s',
      '&:hover': { transform: 'translateY(-2px)' },
    }}>
      <CardContent sx={{ p: { xs: '12px !important', sm: '18px !important' } }}>
        <Stack direction="row" alignItems="center" spacing={0.8} mb={0.5} justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={0.8}>
            <Box sx={{ color, display: 'flex' }}>{icon}</Box>
            <Typography variant="caption" color="text.secondary"
              sx={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
              {label}
            </Typography>
          </Stack>
          {delta !== null && <DeltaBadge delta={delta} label={deltaLabel} />}
        </Stack>
        <Typography sx={{ color, fontWeight: 800, fontSize: { xs: '1.5rem', sm: '1.9rem' }, lineHeight: 1 }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  </Fade>
);

// ─── Rating viz ───────────────────────────────────────────────────────────────
const StarRow = ({ filled, color }) => (
  <Stack direction="row" spacing={0.2} sx={{ width: 78, flexShrink: 0, flexWrap: 'nowrap' }}>
    {Array.from({ length: 5 }, (_, i) => (
      <StarRoundedIcon key={i} sx={{
        fontSize: 13, flexShrink: 0,
        color: i < filled ? color : 'rgba(128,128,128,0.2)',
        filter: i < filled ? `drop-shadow(0 0 3px ${color}88)` : 'none',
        transition: 'color 0.2s',
      }} />
    ))}
  </Stack>
);

const RatingViz = ({ counts, total, p }) => {
  const average = weightedAvg(counts, total);
  return (
    <Box>
      {[5,4,3,2,1].map((star) => {
        const count = counts[star] || 0;
        const pct   = total ? (count/total)*100 : 0;
        const color = RATING_GRADIENT[star - 1];
        return (
          <Stack key={star} direction="row" alignItems="center" spacing={1.2} mb={1.1} sx={{ flexWrap: 'nowrap' }}>
            <StarRow filled={star} color={color} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <LinearProgress variant="determinate" value={pct}
                sx={{ '& .MuiLinearProgress-bar': { background: color, borderRadius: 99 } }} />
            </Box>
            <Typography variant="caption" color="text.secondary"
              sx={{ width: 40, textAlign: 'right', flexShrink: 0, whiteSpace: 'nowrap', fontSize: '0.68rem' }}>
              {count} <span style={{ opacity: 0.5 }}>({Math.round(pct)}%)</span>
            </Typography>
          </Stack>
        );
      })}
      {average && (
        <Box sx={{ display:'inline-flex', alignItems:'center', gap:0.8, mt:1.5, px:1.5, py:0.8,
          background: p.ratingBadgeBg, border: p.ratingBadgeBorder, borderRadius:3 }}>
          <Stack direction="row" spacing={0.2}>
            {Array.from({ length: 5 }, (_, i) => (
              <StarRoundedIcon key={i} sx={{
                fontSize: 13,
                color: i < Math.round(parseFloat(average)) ? '#F9C74F' : 'rgba(128,128,128,0.2)',
              }} />
            ))}
          </Stack>
          <Typography variant="body2" sx={{ fontWeight:700, color:'#F9C74F' }}>{average}</Typography>
          <Typography variant="caption" color="text.secondary">/ 5</Typography>
        </Box>
      )}
    </Box>
  );
};

// ─── Slider viz ───────────────────────────────────────────────────────────────
const SliderViz = ({ counts, total, sliderConfig, p }) => {
  const min = sliderConfig?.min ?? 1;
  const max = sliderConfig?.max ?? 5;
  const average = weightedAvg(counts, total);
  const steps = Array.from({ length: max - min + 1 }, (_, i) => min + i);
  return (
    <Box>
      {[...steps].reverse().map((val) => {
        const count    = counts[val] || 0;
        const pct      = total ? (count/total)*100 : 0;
        const colorIdx = Math.round(((val-min)/(max-min))*(RATING_GRADIENT.length-1));
        return (
          <Stack key={val} direction="row" alignItems="center" spacing={1.5} mb={0.9}>
            <Typography variant="caption" sx={{ width:28, textAlign:'right', color:RATING_GRADIENT[colorIdx], fontWeight:700 }}>
              {val}
            </Typography>
            <Box sx={{ flex:1 }}>
              <LinearProgress variant="determinate" value={pct}
                sx={{ '& .MuiLinearProgress-bar': { background:RATING_GRADIENT[colorIdx], borderRadius:99 } }} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ width: 40, textAlign:'right', fontSize: '0.68rem' }}>
              {count} <span style={{ opacity: 0.5 }}>({Math.round(pct)}%)</span>
            </Typography>
          </Stack>
        );
      })}
      {average && (
        <Box sx={{ display:'inline-flex', alignItems:'center', gap:0.8, mt:1.5, px:1.5, py:0.8,
          background: p.sliderBadgeBg, border: p.sliderBadgeBorder, borderRadius:3 }}>
          <TuneIcon sx={{ color:'#6C63FF', fontSize:15 }} />
          <Typography variant="body2" sx={{ fontWeight:700, color:'#6C63FF' }}>{average}</Typography>
          <Typography variant="caption" color="text.secondary">/ {max}</Typography>
        </Box>
      )}
    </Box>
  );
};

// ─── Emoji viz ────────────────────────────────────────────────────────────────
const EmojiViz = ({ counts, total, type, p }) => {
  const emojiSet = EMOJI_SETS[type] || {};
  const rangeMax = Object.keys(emojiSet).length || 5;
  return (
    <Stack direction="row" gap={0.6} sx={{ flexWrap: 'nowrap', width: '100%' }}>
      {Array.from({ length: rangeMax }, (_,i) => i+1).map((val) => {
        const count = counts[val] || 0;
        const pct   = total ? Math.round((count/total)*100) : 0;
        const src   = getEmojiSrc(type, val);
        return (
          <Box key={val} sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3,
            flex: '1 1 0', minWidth: 0, overflow: 'hidden',
            background: count > 0 ? p.emojiActiveBg    : p.emojiInactiveBg,
            border:     count > 0 ? p.emojiActiveBorder : p.emojiInactiveBorder,
            borderRadius: 3, py: 0.8, px: 0.4,
            opacity: count > 0 ? 1 : 0.35, transition: 'all 0.2s',
          }}>
            {src
              ? <img src={src} alt={`face${val}`} style={{ width: 26, height: 26, objectFit: 'contain', flexShrink: 0 }} />
              : <Typography sx={{ fontSize: '1.1rem', lineHeight: 1 }}>#{val}</Typography>
            }
            <Typography variant="subtitle2" sx={{ fontSize: '0.78rem', lineHeight: 1.2 }}>{count}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.58rem', lineHeight: 1 }}>{pct}%</Typography>
          </Box>
        );
      })}
    </Stack>
  );
};

// ─── Radio viz ────────────────────────────────────────────────────────────────
const RadioViz = ({ counts, total, options }) => {
  const rows = options.length > 0
    ? options.map((o,i) => {
        const slot  = parseInt(o.id ?? i, 10);
        const count = counts[slot] || 0;
        return { key:String(o.id??i), label:o.text||`Option ${i+1}`, count, pct:total?(count/total)*100:0, idx:i };
      })
    : [1,2,3,4,5].map((n) => ({
        key:String(n), label:`Option ${n}`,
        count:counts[n]||0, pct:total?((counts[n]||0)/total)*100:0, idx:n-1,
      }));
  return (
    <Box>
      {rows.map(({ key, label, count, pct, idx }) => (
        <Box key={key} mb={1.4} sx={{ opacity: count===0 ? 0.35 : 1 }}>
          <Stack direction="row" justifyContent="space-between" mb={0.5}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize:'0.78rem' }}>{label}</Typography>
            <Typography variant="body2" sx={{ fontWeight:700, color:RADIO_COLORS[idx%RADIO_COLORS.length], fontSize:'0.78rem', whiteSpace:'nowrap', ml:1 }}>
              {count} <Typography component="span" variant="caption" color="text.secondary">({Math.round(pct)}%)</Typography>
            </Typography>
          </Stack>
          <LinearProgress variant="determinate" value={pct}
            sx={{ '& .MuiLinearProgress-bar': { background:RADIO_COLORS[idx%RADIO_COLORS.length], borderRadius:99 } }} />
        </Box>
      ))}
    </Box>
  );
};

// ─── QuestionResult ───────────────────────────────────────────────────────────
const QuestionResult = ({ group, index, delay, t, p }) => {
  const { text, type, counts, total, options, sliderConfig, url } = group;
  const cardRef = useRef(null);

  const avg     = useMemo(() => type !== 'text' ? weightedAvg(counts, total) : null, [counts, total, type]);
  const health  = getScoreHealth(avg);
  const hc      = HEALTH_CONFIG[health];
  const accent  = CARD_ACCENT[type] || '#6C63FF';

  // Top border color: health for scored questions, type accent for text
  const topColor = avg !== null ? hc.color : accent;

  const dominant = useMemo(() => {
    if (type === 'text') return null;
    const d = dominantAnswer(counts);
    return d <= 5 ? d : null;
  }, [counts, type]);

  const dominantColor = dominant != null ? RATING_GRADIENT[dominant - 1] : null;
  const typeLabel = t[TYPE_LABELS[type]] || type;

  const handleDownloadPng = useCallback(async () => {
    if (!cardRef.current) return;
    const node = cardRef.current;
    const btn  = node.querySelector('.download-btn');
    try {
      if (btn) btn.style.visibility = 'hidden';
      const canvas = await html2canvas(node, {
        scale: 2,
        useCORS: true,
        backgroundColor: p.isDark ? '#151828' : '#FFFFFF',
      });
      const dataUrl = canvas.toDataURL('image/png');
      const safeText = (text || `question-${index + 1}`)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^\w\s-]/g, '')
        .trim()
        .slice(0, 40)
        .replace(/\s+/g, '_');
      const link = document.createElement('a');
      link.download = `Q${index + 1}_${safeText}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Error exporting PNG:', err);
    } finally {
      if (btn) btn.style.visibility = 'visible';
    }
  }, [text, index, p.isDark]);

  const renderViz = () => {
    switch (type) {
      case 'rating':
        return <RatingViz counts={counts} total={total} p={p} />;
      case 'emojis':
      case 'emojis2':
      case 'emojisCat':
      case 'emojispeople':
      case 'emojisnumbers':
      case 'emojislike':
        return <EmojiViz counts={counts} total={total} type={type} p={p} />;
      case 'radio':
        return <RadioViz counts={counts} total={total} options={options||[]} />;
      case 'slider':
        return <SliderViz counts={counts} total={total} sliderConfig={sliderConfig} p={p} />;
      case 'text':
        return (
          <Alert severity="info" sx={{ fontSize:'0.75rem', borderRadius:2 }}>
            {t.textAnswersNote} <code>answers</code>{t.textAnswersNoteSuffix}
          </Alert>
        );
      default:
        return (
          <Typography variant="caption" color="text.secondary">
            {t.typeUnsupported}: '{type}'
          </Typography>
        );
    }
  };

  return (
    <Grow in timeout={500} style={{ transitionDelay:`${delay}ms` }}>
      <Card ref={cardRef} sx={{
        height:'100%',
        background: p.cardBg(topColor),
        borderTop: `3px solid ${topColor} !important`,
        transition:'transform 0.2s, box-shadow 0.2s',
        '&:hover':{ transform:'translateY(-3px)', boxShadow:`0 14px 40px ${topColor}28` },
      }}>
        <CardContent sx={{ p:{ xs:'14px !important', sm:'20px !important' } }}>

          {/* Top badges row */}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1} flexWrap="wrap" gap={0.5}>
            <Stack direction="row" alignItems="center" spacing={0.6}>
              <Box sx={{ px:1, py:0.3, borderRadius:1, background:`${topColor}22` }}>
                <Typography variant="caption" sx={{ color:topColor, fontWeight:800, fontSize:'0.62rem' }}>Q{index+1}</Typography>
              </Box>
              <Tooltip title={t.downloadPng} placement="top">
                <Box
                  className="download-btn"
                  onClick={handleDownloadPng}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 0.5,
                    height: 30, px: 1.2,
                    borderRadius: 2,
                    cursor: 'pointer',
                    color: '#fff',
                    background: `linear-gradient(135deg, ${topColor}, ${topColor}CC)`,
                    boxShadow: `0 3px 10px ${topColor}66`,
                    transition: 'all 0.2s',
                    userSelect: 'none',
                    '&:hover': { transform: 'translateY(-1px) scale(1.04)', boxShadow: `0 5px 14px ${topColor}80` },
                    '&:active': { transform: 'scale(0.97)' },
                  }}
                >
                  <DownloadRoundedIcon sx={{ fontSize: 18 }} />
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#fff', display: { xs: 'none', sm: 'inline' } }}>
                    PNG
                  </Typography>
                </Box>
              </Tooltip>
            </Stack>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.4}>
              {dominant != null && (
                <Chip
                  label={`${t.dominantLabel}: ${dominant}`}
                  size="small"
                  sx={{
                    fontSize: '0.6rem', height: 20,
                    background: `${dominantColor}22`, color: dominantColor,
                    border: `1px solid ${dominantColor}50`, fontWeight: 700,
                  }}
                />
              )}
              <Chip
                icon={TYPE_ICONS[type]}
                label={typeLabel}
                size="small"
                sx={{
                  fontSize:'0.6rem', height:20,
                  background:`${accent}18`, color:accent,
                  border:`1px solid ${accent}30`,
                  '& .MuiChip-icon':{ color:accent },
                }}
              />
              <Chip
                label={`${total} ${t.resp}`}
                size="small"
                sx={{ fontSize:'0.6rem', height:20, background: p.miscChipBg, color:'text.secondary' }}
              />
            </Stack>
          </Stack>

          {/* Score badge row */}
          {avg && (
            <Stack direction="row" alignItems="center" spacing={1} mb={1.2}>
              <ScoreBadge avg={avg} size="lg" />
              <HealthDot avg={parseFloat(avg)} size={7} />
              <Typography variant="caption" sx={{ color: hc.color, fontWeight: 600, fontSize: '0.68rem' }}>
                {t[`health${health.charAt(0).toUpperCase() + health.slice(1)}`] || health}
              </Typography>
            </Stack>
          )}

          {/* Question text */}
          <Typography variant="subtitle2" sx={{ mb: url ? 1.5 : 2, lineHeight:1.45, fontSize:{ xs:'0.86rem', sm:'0.95rem' } }}>
            {text}
          </Typography>

          {/* Question image */}
          {url && (
            <Box sx={{ mb:2, borderRadius:2, overflow:'hidden', lineHeight:0, border:`1px solid ${topColor}22` }}>
              <Box component="img" src={url} alt={text} sx={{
                width:'100%', maxHeight:{ xs:160, sm:200 }, objectFit:'cover', display:'block',
                transition:'transform 0.3s', '&:hover':{ transform:'scale(1.02)' },
              }} />
            </Box>
          )}

          {/* Distribution mini-bar (non-text) */}
          {type !== 'text' && (
            <Box mb={1.5}>
              <MiniDistribution counts={counts} total={total} height={6} />
            </Box>
          )}

          {renderViz()}
        </CardContent>
      </Card>
    </Grow>
  );
};

// ─── Period filter ────────────────────────────────────────────────────────────
const FilterBar = ({ periods, selected, onChange, t }) => (
  <Box sx={{
    display:'flex', gap:0.8, flexWrap:'nowrap', overflowX:'auto', pb:1,
    '&::-webkit-scrollbar':{ height:3 },
    '&::-webkit-scrollbar-thumb':{ background:'rgba(108,99,255,0.45)', borderRadius:2 },
  }}>
    {[
      { key:'all', label: t.filterAll },
      ...periods.map(pk => ({ key:pk, label: periodLabel(pk, t.months) })),
    ].map(({ key, label }) => (
      <Chip key={key} label={label} onClick={() => onChange(key)}
        variant={selected === key ? 'filled' : 'outlined'}
        color={selected === key ? 'primary' : 'default'}
        sx={{ flexShrink:0, fontWeight: selected===key ? 700 : 500, fontSize:'0.78rem' }} />
    ))}
  </Box>
);

// ─── Comments timeline ────────────────────────────────────────────────────────
const CommentsTimeline = ({ surveyId, selected, t, p }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading]   = useState(true);

  const fetchComments = useCallback(async () => {
    setLoading(true);
    try {
      const answersRef = collection(db, 'surveys', surveyId, 'answers');
      let q;
      if (selected !== 'all') {
        const [anio, month] = selected.split('_');
        q = query(answersRef,
          where('anio',  '==', parseInt(anio,  10)),
          where('month', '==', parseInt(month, 10)),
        );
      } else {
        q = answersRef;
      }
      const snap = await getDocs(q);
      const result = [];
      snap.forEach((d) => {
        const data = d.data();
        const comm = (data.comm ?? '').toString().trim();
        if (comm) result.push({ id: d.id, comm, createdBy: data.createdBy ?? '', addRec: data.addRec ?? null, anio: data.anio, month: data.month });
      });
      result.sort((a, b) => (b.addRec?.toMillis?.() ?? 0) - (a.addRec?.toMillis?.() ?? 0));
      setComments(result);
    } catch (err) {
      console.error('Error fetching comments:', err);
    } finally {
      setLoading(false);
    }
  }, [surveyId, selected]);

  useEffect(() => { fetchComments(); }, [fetchComments]);

  return (
    <Fade in timeout={500}>
      <Box sx={{
        border: p.commentBoxBorder, borderRadius:'14px', background: p.commentBoxBg,
        px:{ xs:1.5, sm:3 }, py:2,
        maxHeight:{ xs:420, sm:560 }, overflowY:'auto',
        '&::-webkit-scrollbar':{ width:4 },
        '&::-webkit-scrollbar-thumb':{ background:'rgba(108,99,255,0.35)', borderRadius:2 },
      }}>
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} sx={{ color:'#6C63FF' }} thickness={3} />
          </Box>
        )}
        {!loading && comments.length === 0 && (
          <Box display="flex" flexDirection="column" alignItems="center" py={4} gap={1}>
            <ChatBubbleOutlineIcon sx={{ color: p.isDark ? '#3A3F58' : '#C5C8D8', fontSize:38 }} />
            <Typography color="text.secondary" variant="body2">{t.noComments}</Typography>
          </Box>
        )}
        {!loading && comments.length > 0 && (
          <Box sx={{ position:'relative' }}>
            <Box sx={{
              position:'absolute', left:{ xs:18, sm:22 }, top:0, bottom:0, width:2,
              background: p.timelineLineGrad, borderRadius:99,
            }} />
            {comments.map((c, i) => {
              const color = stringColor(c.createdBy);
              return (
                <Fade key={c.id} in timeout={400} style={{ transitionDelay:`${i * 40}ms` }}>
                  <Stack direction="row" spacing={{ xs:1.2, sm:2 }} mb={i < comments.length - 1 ? 2.5 : 0} sx={{ position:'relative' }}>
                    <Box sx={{ flexShrink:0, zIndex:1 }}>
                      <Avatar sx={{
                        width:{ xs:36, sm:44 }, height:{ xs:36, sm:44 },
                        background:`${color}33`, border:`2px solid ${color}66`,
                        color, fontSize:'0.7rem', fontWeight:700, fontFamily:"'Outfit', sans-serif",
                      }}>
                        {uidInitials(c.createdBy)}
                      </Avatar>
                    </Box>
                    <Box sx={{ flex:1, minWidth:0 }}>
                      <Box sx={{
                        background: p.commentBubbleBg, border: p.commentBubbleBorder,
                        borderRadius:'4px 12px 12px 12px',
                        px:{ xs:1.5, sm:2 }, py:{ xs:1, sm:1.4 },
                        transition:'background 0.15s', '&:hover':{ background: p.commentBubbleHover },
                      }}>
                        <Typography variant="body2" sx={{
                          lineHeight:1.6, wordBreak:'break-word',
                          fontSize:{ xs:'0.82rem', sm:'0.9rem' }, color: p.commentText,
                        }}>
                          {c.comm}
                        </Typography>
                      </Box>
                      <Stack direction="row" alignItems="center" spacing={0.6} mt={0.5} ml={0.5}>
                        <AccessTimeIcon sx={{ fontSize:11, color: p.timeColor }} />
                        <Typography variant="caption" sx={{ color: p.timeColor, fontSize:'0.65rem' }}>
                          {formatDate(c.addRec)}
                        </Typography>
                        {selected === 'all' && (
                          <Chip label={`${t.months[c.month] ?? c.month} ${c.anio}`} size="small"
                            sx={{ height:14, fontSize:'0.58rem', background: p.miscChipBg, color:'text.secondary', ml:0.5 }} />
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                </Fade>
              );
            })}
          </Box>
        )}
      </Box>
    </Fade>
  );
};

// ─── Comment status banner ────────────────────────────────────────────────────
const CommentStatusBanner = ({ enabled, surveyId, selected, t, p }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box mt={5}>
      <Divider sx={{ borderColor: p.dividerColor, mb:3 }} />
      <Stack
        direction={{ xs:'column', sm:'row' }}
        alignItems={{ xs:'flex-start', sm:'center' }}
        justifyContent="space-between"
        spacing={1.5}
        mb={open && enabled ? 2 : 0}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{
            width:40, height:40, borderRadius:'10px',
            background: enabled ? 'rgba(67,233,123,0.12)' : 'rgba(255,107,107,0.1)',
            border: enabled ? '1px solid rgba(67,233,123,0.3)' : '1px solid rgba(255,107,107,0.25)',
            display:'flex', alignItems:'center', justifyContent:'center',
          }}>
            {enabled
              ? <CheckCircleIcon sx={{ color:'#43E97B', fontSize:22 }} />
              : <CancelIcon sx={{ color:'#FF6B6B', fontSize:22 }} />
            }
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ fontSize:'0.95rem', color: enabled ? '#43E97B' : '#FF6B6B' }}>
              {enabled ? t.commentsEnabled : t.commentsDisabled}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform:'none', letterSpacing:0 }}>
              {enabled ? t.commentsEnabledDesc : t.commentsDisabledDesc}
            </Typography>
          </Box>
        </Stack>
        {enabled && (
          <Button variant={open ? 'contained' : 'outlined'} color="primary" size="small"
            startIcon={<CommentIcon />} endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setOpen((prev) => !prev)}
            sx={{ flexShrink:0, borderRadius:3, textTransform:'none', fontWeight:700, fontSize:'0.82rem' }}>
            {open ? t.hideComments : t.showComments}
          </Button>
        )}
      </Stack>
      {enabled && (
        <Collapse in={open} unmountOnExit>
          <CommentsTimeline surveyId={surveyId} selected={selected} t={t} p={p} />
        </Collapse>
      )}
    </Box>
  );
};

// ─── Theme toggle button ──────────────────────────────────────────────────────
const ThemeToggle = ({ mode, onToggle, t, p }) => (
  <Tooltip title={mode === 'light' ? t.toggleDark : t.toggleLight} placement="left">
    <IconButton onClick={onToggle} size="small" sx={{
      width:36, height:36, borderRadius:'10px',
      background: p.toggleBg, color: p.toggleColor,
      border: `1px solid ${p.toggleColor}30`,
      transition:'all 0.25s',
      '&:hover':{ background: p.toggleHover, transform:'rotate(20deg) scale(1.08)' },
    }}>
      {mode === 'light'
        ? <DarkModeRoundedIcon sx={{ fontSize:18 }} />
        : <WbSunnyRoundedIcon  sx={{ fontSize:18 }} />
      }
    </IconButton>
  </Tooltip>
);

// ─── Main ─────────────────────────────────────────────────────────────────────
const Resultado = ({ surveyId }) => {
  const t = useTranslations();

  const [themeMode, setThemeMode] = useState('light');
  const toggleMode = useCallback(() => setThemeMode((m) => m === 'light' ? 'dark' : 'light'), []);

  const theme = useMemo(() => buildTheme(themeMode), [themeMode]);
  const p     = useMemo(() => getPalette(themeMode), [themeMode]);

  const [surveyQuestions, setSurveyQuestions] = useState(null);
  const [surveyMeta, setSurveyMeta]           = useState({ title: '', description: '' });
  const [showComment, setShowComment]         = useState(false);
  const [sumByPeriod, setSumByPeriod]         = useState({});
  const [allPeriods, setAllPeriods]           = useState([]);
  const [selected, setSelected]               = useState(currentPeriodKey());
  const [sortMode, setSortMode]               = useState('orden');
  const [viewMode, setViewMode]               = useState('cards');   // 'cards' | 'table'
  const [healthFilter, setHealthFilter]       = useState('all');
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState('');

  // Reset health filter when period changes
  const handlePeriodChange = useCallback((key) => {
    setSelected(key);
    setHealthFilter('all');
  }, []);

  useEffect(() => {
    if (!surveyId) return;
    const fetchAll = async () => {
      try {
        const surveySnap = await getDoc(doc(db, 'surveys', surveyId));
        if (!surveySnap.exists()) { setError(t.errorSurvey); setLoading(false); return; }
        const surveyData = surveySnap.data();
        setSurveyQuestions(surveyData.questions || {});
        setShowComment(surveyData.showComment === true);
        setSurveyMeta({ title: surveyData.title ?? '', description: surveyData.description ?? '' });

        const sumSnap  = await getDocs(collection(db, 'surveys', surveyId, 'answerSum'));
        const byPeriod = {};
        sumSnap.docs.forEach((d) => {
          const data   = d.data();
          const period = periodKeyFromData(data);
          if (!byPeriod[period]) byPeriod[period] = [];
          byPeriod[period].push(data);
        });
        const sorted = Object.keys(byPeriod).sort((a,b) => b.localeCompare(a));
        setSumByPeriod(byPeriod);
        setAllPeriods(sorted);
        const cur = currentPeriodKey();
        if (!byPeriod[cur] && sorted.length > 0) setSelected(sorted[0]);
      } catch (err) {
        console.error(err);
        setError(t.errorGeneric);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [surveyId]);

  const filteredDocs = useMemo(() => {
    if (selected === 'all') return Object.values(sumByPeriod).flat();
    return sumByPeriod[selected] || [];
  }, [sumByPeriod, selected]);

  const groups = useMemo(() => {
    if (!surveyQuestions) return [];
    const base = aggregateGroups(surveyQuestions, filteredDocs);
    if (sortMode === 'result') {
      return [...base].sort((a, b) => {
        if (a.type === 'text' && b.type !== 'text') return  1;
        if (b.type === 'text' && a.type !== 'text') return -1;
        const da = dominantAnswer(a.counts);
        const db = dominantAnswer(b.counts);
        if (da !== db) return da - db;
        return a.orden - b.orden;
      });
    }
    return base;
  }, [surveyQuestions, filteredDocs, sortMode]);

  // Previous period groups for delta calculation
  const prevGroups = useMemo(() => {
    if (!surveyQuestions || selected === 'all') return [];
    const prevKey = previousPeriodKey(selected);
    if (!prevKey || !sumByPeriod[prevKey]) return [];
    return aggregateGroups(surveyQuestions, sumByPeriod[prevKey]);
  }, [surveyQuestions, selected, sumByPeriod]);

  // Health counts for filter chips
  const healthCounts = useMemo(() => {
    const result = { good: 0, fair: 0, poor: 0 };
    groups.forEach(g => {
      if (g.type === 'text') return;
      const avg = weightedAvg(g.counts, g.total);
      if (!avg) return;
      const h = getScoreHealth(avg);
      if (h !== 'neutral') result[h]++;
    });
    return result;
  }, [groups]);

  // Apply health filter
  const filteredGroups = useMemo(() => {
    if (healthFilter === 'all') return groups;
    return groups.filter(g => {
      if (g.type === 'text') return false; // text has no health score
      const avg = weightedAvg(g.counts, g.total);
      return getScoreHealth(avg) === healthFilter;
    });
  }, [groups, healthFilter]);

  const { totalResponses, globalAvg, prevTotalResponses, prevGlobalAvg } = useMemo(() => {
    const supported = groups.filter(g => g.type !== 'text');
    const totals    = supported.map(g => g.total);
    const maxTotal  = totals.length ? Math.max(...totals) : 0;
    const avg       = computeGlobalAvg(groups);

    const prevSupported = prevGroups.filter(g => g.type !== 'text');
    const prevTotals    = prevSupported.map(g => g.total);
    const prevMax       = prevTotals.length ? Math.max(...prevTotals) : 0;
    const prevAvg       = computeGlobalAvg(prevGroups);

    return {
      totalResponses:    maxTotal,
      globalAvg:         avg,
      prevTotalResponses: prevMax || null,
      prevGlobalAvg:      prevAvg,
    };
  }, [groups, prevGroups]);

  const responseDelta = useMemo(() => {
    if (!prevTotalResponses || selected === 'all') return null;
    return (totalResponses - prevTotalResponses).toString();
  }, [totalResponses, prevTotalResponses, selected]);

  const avgDelta = useMemo(() => {
    if (!prevGlobalAvg || !globalAvg || selected === 'all') return null;
    return (parseFloat(globalAvg) - parseFloat(prevGlobalAvg)).toFixed(2);
  }, [globalAvg, prevGlobalAvg, selected]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap');`}</style>

      <Box sx={{
        minHeight: '100vh',
        background: p.pageBg,
        px: { xs:1.5, sm:3, md:4 },
        py: { xs:2.5, sm:4 },
        transition: 'background 0.35s ease',
      }}>

        {/* Header */}
        <Fade in timeout={400}>
          <Box mb={3.5}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
              <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ minWidth:0, flex:1 }}>
                <Box sx={{
                  width:40, height:40, borderRadius:'12px', flexShrink:0, mt:0.3,
                  background: p.headerLogoBg,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  boxShadow:'0 4px 16px rgba(108,99,255,0.4)',
                }}>
                  <BarChartIcon sx={{ color:'#fff', fontSize:22 }} />
                </Box>
                <Box sx={{ minWidth:0 }}>
                  <Typography variant="h5" sx={{
                    fontSize:{ xs:'1.15rem', sm:'1.45rem' }, color: p.commentText,
                    whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis',
                  }}>
                    {surveyMeta.title || t.surveyTitle}
                  </Typography>
                  {surveyMeta.description && (
                    <Typography variant="body2" color="text.secondary"
                      sx={{ mt:0.3, fontSize:'0.82rem', lineHeight:1.5 }}>
                      {surveyMeta.description}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary"
                    sx={{ textTransform:'none', letterSpacing:0, fontSize:'0.7rem', opacity:0.6 }}>
                    ID: {surveyId}
                  </Typography>
                </Box>
              </Stack>
              <Box sx={{ flexShrink:0, mt:0.3 }}>
                <ThemeToggle mode={themeMode} onToggle={toggleMode} t={t} p={p} />
              </Box>
            </Stack>
          </Box>
        </Fade>

        {/* Loading */}
        {loading && (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="50vh" gap={2}>
            <CircularProgress sx={{ color:'#6C63FF' }} size={46} thickness={3} />
            <Typography color="text.secondary">{t.loading}</Typography>
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error" sx={{ borderRadius:3, mt:4 }}>{error}</Alert>
        )}

        {!loading && !error && allPeriods.length === 0 && (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="50vh" gap={2}>
            <Typography sx={{ fontSize:'3rem' }}>📭</Typography>
            <Typography color="text.secondary">{t.noData}</Typography>
          </Box>
        )}

        {!loading && !error && allPeriods.length > 0 && (
          <>
            {/* Period filter + Sort + View controls */}
            <Stack direction="row" alignItems="flex-start" spacing={1.5} mb={2}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <FilterBar periods={allPeriods} selected={selected} onChange={handlePeriodChange} t={t} />
              </Box>
              <Stack direction="row" spacing={0.8} sx={{ flexShrink: 0, pt: 0.4 }}>
                <ControlPill
                  value={sortMode}
                  onChange={setSortMode}
                  options={[
                    { key: 'orden',  icon: <FilterListIcon sx={{ fontSize: 14 }} />, label: t.sortByOrder,  tooltip: '' },
                    { key: 'result', icon: <SortIcon       sx={{ fontSize: 14 }} />, label: t.sortByResult, tooltip: t.sortTooltip },
                  ]}
                />
                <ControlPill
                  value={viewMode}
                  onChange={setViewMode}
                  options={[
                    { key: 'cards', icon: <ViewModuleIcon  sx={{ fontSize: 14 }} />, label: t.viewCards },
                    { key: 'table', icon: <TableRowsIcon   sx={{ fontSize: 14 }} />, label: t.viewTable },
                  ]}
                />
              </Stack>
            </Stack>

            {/* Stat cards */}
            <Stack direction="row" spacing={{ xs:1, sm:1.5 }} mb={2.5}>
              <StatCard
                icon={<PeopleAltIcon sx={{ fontSize:18 }} />}
                label={t.statResponses} value={totalResponses}
                color="#6C63FF" delay={0} p={p}
                delta={responseDelta}
                deltaLabel={t.vsLastPeriod}
              />
              <StatCard
                icon={<StarRoundedIcon sx={{ fontSize:18 }} />}
                label={t.statAverage} value={globalAvg ?? '—'}
                color="#F9C74F" delay={100} p={p}
                delta={avgDelta}
                deltaLabel={t.vsLastPeriod}
              />
              <StatCard
                icon={<QuizIcon sx={{ fontSize:18 }} />}
                label={t.statQuestions} value={groups.length}
                color="#43E97B" delay={200} p={p}
              />
            </Stack>

            {/* Health filter */}
            <Box mb={2}>
              <HealthFilter
                value={healthFilter}
                onChange={setHealthFilter}
                healthCounts={healthCounts}
                t={t}
                p={p}
              />
            </Box>

            {/* Content: Cards or Table */}
            {viewMode === 'cards' ? (
              <Grid container spacing={{ xs:1.5, sm:2 }}>
                {filteredGroups.map((group, i) => (
                  <Grid item xs={12} sm={6} lg={4} key={group.orden}>
                    <QuestionResult group={group} index={groups.indexOf(group)} delay={i * 60} t={t} p={p} />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <CompactTable
                groups={filteredGroups}
                t={t}
                p={p}
                surveyTitle={surveyMeta.title || surveyId}
                periodLabelText={selected === 'all' ? t.filterAll : periodLabel(selected, t.months)}
              />
            )}

            {healthFilter !== 'all' && filteredGroups.length === 0 && (
              <Box display="flex" flexDirection="column" alignItems="center" py={8} gap={1}>
                <Typography sx={{ fontSize: '2rem' }}>🔍</Typography>
                <Typography color="text.secondary" variant="body2">
                  {t.healthAll}
                </Typography>
              </Box>
            )}

            <CommentStatusBanner
              enabled={showComment}
              surveyId={surveyId}
              selected={selected}
              t={t}
              p={p}
            />
          </>
        )}

      </Box>
    </ThemeProvider>
  );
};

export default Resultado;